// The Summary tab: KPIs, highlights that point to where to act, the trend, and one breakdown.
import { useMemo, useState } from "react";
import { getCustomerHealth, getSalesmanScorecard } from "../../distflowAPI/salesAnalyticsApi";
import Highlights from "./Highlights";
import SalesExplorer from "./SalesExplorer";
import SalesTrend from "./SalesTrend";
import { customerLevelFilters } from "./drillDown";
import { count, money, pct, tone } from "./format";
import { categoriesHighlight, customersHighlight, openDaysHighlight, salesmenHighlight } from "./highlightRules";
import { COMPARE_OPTIONS } from "./urlState";
import useSideReport from "./useSideReport";

const LOW_COST_COVERAGE = 95;
const number = (value) => Number(value || 0);

function Change({ value, absolute = false }) {
  if (value == null) return <b className="sa-kpi-delta sa-kpi-delta--flat">No baseline</b>;
  const amount = number(value);
  const direction = amount > 0 ? "up" : amount < 0 ? "down" : null;
  return <b className={`sa-kpi-delta sa-kpi-delta--${tone(amount)}`}>
    {direction && <><span aria-hidden="true">{amount > 0 ? "▲" : "▼"} </span><span className="sa-visually-hidden">{direction} </span></>}
    {absolute ? count(Math.abs(amount)) : pct(Math.abs(amount))}
  </b>;
}

function Kpi({ label, value, change, absolute, hasComparison, base, note }) {
  return <div className="sa-kpi">
    <span className="sa-kpi-label">{label}</span>
    <strong className="sa-kpi-value">{value}</strong>
    <span className="sa-kpi-change">
      {hasComparison && <Change value={change} absolute={absolute} />}
      {base && <span>{hasComparison ? " " : ""}{base}</span>}
    </span>
    {note && <span className="sa-kpi-note">{note}</span>}
  </div>;
}

export default function Summary({ report, view, applied, filters, ready, onChange, onDrill }) {
  const [movement, setMovement] = useState("all");
  const summary = report.summary || {};
  const hasComparison = Boolean(report.comparison);
  const comparisonLabel = COMPARE_OPTIONS.find((item) => item.value === view.compare)?.label || "Comparison";

  const healthParams = useMemo(() => ({
    as_of: report.date_range?.end_date, territory_id: applied.territory_id, filters: customerLevelFilters(filters),
  }), [report.date_range?.end_date, applied.territory_id, filters]);
  const scorecardParams = useMemo(() => ({
    start_date: applied.start_date, end_date: applied.end_date, territory_id: applied.territory_id,
    comparison: applied.comparison, comparison_start_date: applied.comparison_start_date, comparison_end_date: applied.comparison_end_date,
  }), [applied]);
  // Side reports wait for the main report, so the numbers on screen come first.
  const health = useSideReport(getCustomerHealth, healthParams, ready && Boolean(healthParams.as_of));
  const scorecard = useSideReport(getSalesmanScorecard, scorecardParams, ready);

  const highlights = [
    { key: "customers", kicker: "Customers", loading: health.loading, highlight: customersHighlight(health.data) },
    { key: "days", kicker: "Open days", highlight: openDaysHighlight(report) },
    { key: "categories", kicker: "Products", highlight: categoriesHighlight(report, health.data) },
    { key: "salesmen", kicker: "Salesmen", loading: scorecard.loading, highlight: salesmenHighlight(scorecard.data, view.salesman) },
  ];

  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }
  function act(action) {
    if (action.tab) { onChange({ tab: action.tab }); return; }
    if (action.breakdown) {
      if (action.breakdown !== view.by) onChange({ by: action.breakdown });
      setMovement(action.movement || "all");
      scrollTo("sales-analytics-breakdown");
      return;
    }
    if (action.target === "trend") scrollTo("sales-analytics-trend");
  }

  const before = (value, format) => (hasComparison && value != null ? `vs ${format(value)}` : null);
  const coverage = summary.cost_coverage_percent;
  const openDays = summary.open_days;
  return <>
    <section className="sa-kpis" aria-label="Sales summary">
      <Kpi label="Sales" value={money(summary.total_sales)} change={summary.sales_change_percent} hasComparison={hasComparison}
        base={before(summary.previous_total_sales, money)} />
      {openDays !== undefined && <Kpi label="Per open day" value={summary.sales_per_open_day == null ? "—" : money(summary.sales_per_open_day)}
        change={summary.sales_per_open_day_change_percent} hasComparison={hasComparison}
        base={`${count(openDays)} open ${Number(openDays) === 1 ? "day" : "days"}${hasComparison && summary.previous_open_days != null ? ` vs ${count(summary.previous_open_days)}` : ""}`} />}
      {summary.gross_profit !== undefined && <Kpi label="Gross profit" value={summary.gross_profit == null ? "—" : money(summary.gross_profit)}
        change={summary.gross_profit_change_percent} hasComparison={hasComparison && summary.gross_profit != null}
        base={summary.margin_percent != null ? `${pct(summary.margin_percent)} margin` : "No product costs recorded"}
        note={coverage != null && number(coverage) < LOW_COST_COVERAGE ? `Cost known for ${pct(coverage, 0)} of item sales` : null} />}
      <Kpi label="Orders" value={count(summary.total_orders)} change={summary.total_orders_change_percent} hasComparison={hasComparison}
        base={before(summary.previous_total_orders, count)} />
      <Kpi label="Active customers" value={count(summary.total_customers)} hasComparison={hasComparison}
        change={hasComparison && summary.previous_total_customers != null ? number(summary.total_customers) - number(summary.previous_total_customers) : null}
        absolute base={before(summary.previous_total_customers, count)} />
      <Kpi label="Average order" value={money(summary.average_order_size)} change={summary.average_order_size_change_percent} hasComparison={hasComparison}
        base={before(summary.previous_average_order_size, money)} />
    </section>
    <Highlights items={highlights} onAction={act} />
    <SalesTrend id="sales-analytics-trend" trend={report.trend} comparisonLabel={hasComparison ? comparisonLabel : undefined}
      openDays={openDays} perOpenDay={summary.sales_per_open_day} />
    <SalesExplorer id="sales-analytics-breakdown" report={report} dimension={view.by} onDimensionChange={(by) => { setMovement("all"); onChange({ by }); }}
      movement={movement} onMovementChange={setMovement} onDrill={onDrill} />
  </>;
}
